// Unit + formatting helpers. Data is always STORED in imperial (miles, pounds).
// These helpers convert for DISPLAY / INPUT based on the user's `units` preference.
// Scope (per product decision): runs (km) and weights (kg) only. Nutrition stays as-is.

export const KM_PER_MI = 1.60934;
export const KG_PER_LB = 0.453592;
export const CM_PER_IN = 2.54;

export const isMetric = (user) => user?.units === "metric";

export const distUnit = (user) => (isMetric(user) ? "km" : "mi");
export const weightUnit = (user) => (isMetric(user) ? "kg" : "lb");
export const heightUnit = (user) => (isMetric(user) ? "cm" : "in");

// ----- Distance (stored as miles) -----
export const miToDisplay = (mi, user) => (isMetric(user) ? (mi || 0) * KM_PER_MI : (mi || 0));
export const displayToMi = (val, user) => (isMetric(user) ? (val || 0) / KM_PER_MI : (val || 0));
export const fmtDistance = (mi, user, digits = 2) =>
  `${miToDisplay(mi, user).toFixed(digits)} ${distUnit(user)}`;

// ----- Pace -----
// Formats a decimal pace value (minutes per unit) as m:ss.
export const fmtPaceValue = (decimalPace) => {
  if (!decimalPace || decimalPace <= 0 || !isFinite(decimalPace)) return "0:00";
  const m = Math.floor(decimalPace);
  const s = Math.round((decimalPace - m) * 60);
  if (s === 60) return `${m + 1}:00`;
  return `${m}:${String(s).padStart(2, "0")}`;
};

// Given a stored pace in minutes-per-MILE, return "m:ss/mi" or "m:ss/km" for the user's units.
export const fmtStoredPace = (paceMinPerMi, user) => {
  const perUnit = isMetric(user) ? (paceMinPerMi || 0) / KM_PER_MI : (paceMinPerMi || 0);
  return `${fmtPaceValue(perUnit)}/${distUnit(user)}`;
};

// Live pace from raw miles + seconds (computes min/mi then converts to the user's unit).
export const fmtLivePace = (mi, sec, user) => {
  if (!mi || mi <= 0) return `0:00/${distUnit(user)}`;
  const paceMinPerMi = (sec / 60) / mi;
  return fmtStoredPace(paceMinPerMi, user);
};

// ----- Weight (stored as pounds) -----
export const lbToDisplay = (lb, user) =>
  lb === "" || lb === null || lb === undefined ? "" : (isMetric(user) ? (lb * KG_PER_LB) : lb);
export const displayToLb = (val, user) => {
  const n = parseFloat(val);
  if (isNaN(n)) return null;
  return isMetric(user) ? n / KG_PER_LB : n;
};
export const fmtWeight = (lb, user, digits = 0) => {
  if (lb === null || lb === undefined || lb === "") return "—";
  return `${miToNum(lbToDisplay(lb, user), digits)} ${weightUnit(user)}`;
};
const miToNum = (v, digits) => (Math.round(v * 10 ** digits) / 10 ** digits);

// ----- Height (stored as inches) -----
export const inToDisplay = (inches, user) =>
  inches === "" || inches === null || inches === undefined ? "" : (isMetric(user) ? (inches * CM_PER_IN) : inches);
export const displayToIn = (val, user) => {
  const n = parseFloat(val);
  if (isNaN(n)) return null;
  return isMetric(user) ? n / CM_PER_IN : n;
};

// ----- Elevation (stored as feet) -----
export const FT_PER_M = 3.28084;
export const elevUnit = (user) => (isMetric(user) ? "m" : "ft");
export const ftToDisplay = (ft, user) => (isMetric(user) ? (ft || 0) / FT_PER_M : (ft || 0));
export const fmtElevation = (ft, user) => `${Math.round(ftToDisplay(ft, user))} ${elevUnit(user)}`;
